import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar as CalendarIcon, PackageCheck, Users } from "lucide-react";
import { Cronograma } from "./Cronograma";
import { Entregas } from "./Entregas";
import { AgendaEquipe } from "./AgendaEquipe";
import { useAuth } from "../hooks/useAuth";

/**
 * Hub de "Agenda": unifica num menu só as visões de tempo (decisão da Rec, jul/2026).
 * Abas: Cronograma (marcos dos projetos + eventos da empresa no MESMO calendário —
 * a aba "Eventos" separada foi fundida aqui em jul/2026) e Entregas (pauta de
 * entregas por cliente, colorida por tipo de peça — Fase 3).
 * Entregas é restrita a gestor/admin: usa a data de entrega combinada com o
 * cliente, campo que já é oculto do funcionário/suporte em todo o resto do app.
 * A aba ativa fica na URL (?aba=) pra deep-link e pro redirect de /cronograma funcionar.
 * Links antigos com ?aba=eventos caem no Cronograma pelo fallback.
 */
type Aba = "cronograma" | "entregas" | "agenda";
const ABAS: Array<{ id: Aba; label: string; icon: any }> = [
  { id: "cronograma", label: "Cronograma", icon: CalendarIcon },
  { id: "agenda", label: "Agenda da equipe", icon: Users },
  { id: "entregas", label: "Entregas", icon: PackageCheck },
];
// Abas só de gestão (usam dados que o funcionário não vê / é visão de controle).
const SO_GESTAO: Aba[] = ["entregas", "agenda"];

export function CalendarioHub() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const podeGestao = can(["gestor", "admin"]);
  const abasVisiveis = useMemo(() => ABAS.filter((a) => !SO_GESTAO.includes(a.id) || podeGestao), [podeGestao]);
  const bruto = params.get("aba") as Aba | null;
  const aba: Aba = bruto && abasVisiveis.some((a) => a.id === bruto) ? bruto : "cronograma";

  function trocar(a: Aba) {
    const p = new URLSearchParams(params);
    p.set("aba", a);
    setParams(p, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
        {abasVisiveis.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              onClick={() => trocar(a.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
                aba === a.id
                  ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              }`}
            >
              <Icon size={14} /> {a.label}
            </button>
          );
        })}
      </div>

      {aba === "cronograma" && <Cronograma />}
      {aba === "agenda" && <AgendaEquipe />}
      {aba === "entregas" && <Entregas />}
    </div>
  );
}
